/**
 * 语音发送纯逻辑单点（任务 14.2b）：录音 MIME 协商 + codec/文件名映射。
 * 录制 UI（主线程）与发送链（Worker）共用，保证「录出什么格式 → envelope 标
 * 什么 codec → 上传什么扩展名」一个口径。
 *
 * MIME 偏好顺序刻意 AAC 优先（docs/compat.md「语音录制」节实测结论）：
 * - audio/mp4(AAC) 全端可播（iOS Safari 14+ / 旧版 Flutter 客户端 codec 恒 'aac'）；
 *   webm/opus 在旧 iOS 上播不了——能录 AAC 就录 AAC。
 * - 安卓 Chrome < 126 无 audio/mp4 录制，回落 webm/opus（接收端兼容性见 compat.md，
 *   真机差异待人工回归）。
 * 上传不压缩不改后缀（后端对非图片原样存、扩展名取自 filename，uploadsUtils.go 实锤）。
 */

/** 录音格式偏好（先 AAC 后 opus；codecs 全称优先，裸容器兜底） */
export const RECORDING_MIME_CANDIDATES: readonly string[] = [
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
  'audio/webm;codecs=opus',
  'audio/webm',
];

/** 选第一个受支持的录音 MIME；全不支持返回 ''（调用方交给 MediaRecorder 默认） */
export function pickRecordingMime(isTypeSupported: (type: string) => boolean): string {
  for (const t of RECORDING_MIME_CANDIDATES) {
    try {
      if (isTypeSupported(t)) return t;
    } catch {
      /* 个别实现对带参数 MIME 抛异常：当不支持继续试下一个 */
    }
  }
  return '';
}

/** envelope codec 字段（旧版恒 'aac'；webm/ogg 实录为 opus 须如实标注供接收端判断） */
export function voiceCodecOf(mime: string): string {
  const m = mime.toLowerCase();
  if (m.includes('webm') || m.includes('ogg') || m.includes('opus')) return 'opus';
  return 'aac';
}

/** 上传文件名（后端按最后一个 . 取扩展名、非图片不改 .acg——必须给对扩展名） */
export function voiceFilenameFor(mime: string): string {
  const m = mime.toLowerCase();
  if (m.includes('webm')) return 'voice.webm';
  if (m.includes('ogg')) return 'voice.ogg';
  return 'voice.m4a';
}

/** 最短录音时长（旧版 700ms 同口径，短于此丢弃并提示「录音时间太短」） */
export const MIN_VOICE_DURATION_MS = 700;
